import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/lib/lang-context";
import { SeoHead } from "@/lib/seo";
import { PRIVACY_IT, PRIVACY_EN } from "@/lib/privacy-text";
import { getConsent, setConsent } from "@/lib/consent";
import { useState } from "react";
import { Breadcrumb, breadcrumbJsonLd } from "@/components/Breadcrumb";

/**
 * Static legal page. Content lives in `lib/privacy-text.js` so it can be
 * updated without touching presentation. Includes an inline "manage your
 * choice" widget so the user can revoke / grant analytics consent without
 * having to clear cookies manually (UX best practice + Italian DPA guidance).
 */
const Privacy = () => {
  const { lang, t } = useLang();
  const data = lang === "it" ? PRIVACY_IT : PRIVACY_EN;
  const [consent, setConsentState] = useState(getConsent());

  const updateChoice = (state) => {
    setConsent(state);
    setConsentState(state);
  };

  return (
    <main
      data-testid="privacy-page"
      className="max-w-3xl mx-auto px-6 md:px-10 py-12"
    >
      <Breadcrumb items={[
        { label: "Home", to: "/" },
        { label: data.title },
      ]} />
      <Link
        to="/"
        data-testid="privacy-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#6C706D] hover:text-[#282C2A] transition-colors mt-6"
      >
        <ArrowLeft size={13} strokeWidth={1.5} />
        {t("privacy.back")}
      </Link>

      <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-[#282C2A] mt-6 leading-tight">
        {data.title}
      </h1>
      <p className="label-eyebrow mt-3">{data.updated}</p>

      <p className="mt-8 text-sm leading-relaxed text-[#282C2A]">
        {data.intro}
      </p>

      <article className="mt-10 space-y-8">
        {data.sections.map((s, i) => (
          <section key={i} data-testid={`privacy-section-${i}`}>
            <h2 className="font-serif-display text-2xl text-[#282C2A] mb-3">
              {s.h}
            </h2>
            {s.p && (
              <p className="text-sm leading-relaxed text-[#282C2A]">{s.p}</p>
            )}
            {s.list && (
              <ul className="mt-3 space-y-2 text-sm text-[#282C2A]">
                {s.list.map((item, j) => (
                  <li key={j} className="flex gap-3 leading-relaxed">
                    <span className="text-[#4A5D23] mt-1 shrink-0">▪</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
            {s.footer && (
              <p className="mt-3 text-sm leading-relaxed text-[#282C2A]">
                {s.footer}
              </p>
            )}
          </section>
        ))}
      </article>

      {/* Live consent widget — lets the user change their mind without
          clearing cookies. Required by Italian DPA guidance (Provv. 231/2021). */}
      <section
        data-testid="privacy-consent-widget"
        className="mt-12 border-2 border-[#282C2A] bg-[#F0EFEB] p-6"
      >
        <div className="label-eyebrow mb-2">{t("privacy.consent.eyebrow")}</div>
        <h3 className="font-serif-display text-xl text-[#282C2A]">
          {t("privacy.consent.title")}
        </h3>
        <p className="mt-2 text-sm text-[#6C706D] leading-relaxed">
          {t("privacy.consent.body")}
        </p>
        <div className="mt-3 text-xs font-mono uppercase tracking-widest text-[#282C2A]">
          {t("privacy.consent.current")}:{" "}
          <span data-testid="privacy-consent-state">
            {consent === "granted"
              ? t("privacy.consent.granted")
              : consent === "denied"
              ? t("privacy.consent.denied")
              : t("privacy.consent.notSet")}
          </span>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => updateChoice("denied")}
            data-testid="privacy-consent-reject"
            className="flex-1 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest border border-[#282C2A] text-[#282C2A] hover:bg-[#282C2A] hover:text-white transition-colors"
          >
            {t("cookie.reject")}
          </button>
          <button
            type="button"
            onClick={() => updateChoice("granted")}
            data-testid="privacy-consent-accept"
            className="flex-1 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest bg-[#4A5D23] text-white hover:bg-[#3B4A1C] transition-colors"
          >
            {t("cookie.accept")}
          </button>
        </div>
      </section>
    </main>
  );
};

export default Privacy;
